// src/components/Katie/Katie.jsx
import { useEffect, useRef, useState } from "react";
import Phaser from "phaser";
import gameConfig from "./phaser/gameConfig";

// import question sets
import QUESTIONS_EN from "./phaser/data/questions_en.js"; // <-- copy of current questions.js
import QUESTIONS_ZH from "./phaser/data/questions_zh.js"; // <-- new translated file

export default function Katie({ username = "Guest", onExit }) {
    const mountRef = useRef(null);
    const gameRef = useRef(null);

    // language (persist last choice)
    const [lang, setLang] = useState(() => localStorage.getItem("katie_lang") || "");
    const [started, setStarted] = useState(false);

    // takeover full-screen BG video
    const [takeover, setTakeover] = useState({
        src: null, visible: false, playOnce: false, cb: null,
        opacity: 1, ms: 600,
        muted: true,
        volume: 1
    });
    // fade the whole white app panel (header + card + footer)
    const [appFade, setAppFade] = useState({ opacity: 1, ms: 600 });

    const handleExit = () => {
        if (typeof onExit === "function") return onExit();
        window.location.assign("/Dashboard");
    };

    // block mobile
    const computeBlocked = () => {
        const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
        const isMobileUA = /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(ua);
        const w = typeof window !== "undefined" ? window.innerWidth : 1920;
        return isMobileUA || w < 900;
    };
    const [blocked, setBlocked] = useState(computeBlocked);
    useEffect(() => {
        const onResize = () => setBlocked(computeBlocked());
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
        };
    }, []);
    useEffect(() => {
        if (blocked && gameRef.current) {
            try { gameRef.current.destroy(true); } catch { }
            gameRef.current = null;
        }
    }, [blocked]);

    // boot Phaser only AFTER language is selected and "Start" pressed
    useEffect(() => {
        if (blocked || !started || !lang) return;

        const cfg = gameConfig(mountRef.current);
        const game = new Phaser.Game(cfg);

        // pick question set
        const questions = (lang === "zh" ? QUESTIONS_ZH : QUESTIONS_EN)({ lang });

        game.registry.set("ctx", {
            username,
            localTime: new Date().toLocaleTimeString(),
            onExit: handleExit,
            kConfig: { normalCount: 5 },
            lang, // expose current language to scenes
        });
        game.registry.set("questions", questions);

        // video takeover bridges (unchanged)
        game.registry.set("startBgTakeover", (src) =>
            setTakeover({ src, visible: true, playOnce: false, cb: null, opacity: 1, ms: 600 })
        );
        game.registry.set("endBgTakeover", () =>
            setTakeover({ src: null, visible: false, playOnce: false, cb: null, opacity: 1, ms: 600 })
        );
        game.registry.set("startBgTakeoverOnce", (src, onEnd, fadeInMs = 1400, opts = {}) => {
            setTakeover({
                src, visible: true, playOnce: true,
                cb: typeof onEnd === "function" ? onEnd : null,
                opacity: 0, ms: fadeInMs,
                muted: opts.muted ?? true,
                volume: typeof opts.volume === "number" ? opts.volume : 1
            });
            requestAnimationFrame(() => setTakeover(t => ({ ...t, opacity: 1 })));
        });
        game.registry.set("fadeAppTo", (opacity = 1, ms = 600) => setAppFade({ opacity, ms }));

        gameRef.current = game;
        const resize = () => {
            const w = Math.min(1100, mountRef.current?.clientWidth || 1100);
            const h = Math.round((700 / 1000) * w);
            game.scale.resize(w, h);
        };
        resize();
        window.addEventListener("resize", resize);

        // kick Test scene
        game.scene.start("Boot"); // (or "Test" if you don't have a Boot scene)

        return () => {
            window.removeEventListener("resize", resize);
            try { game.destroy(true); } catch { }
            gameRef.current = null;
        };
    }, [blocked, started, lang, username, onExit]);

    if (blocked) {
        return (
            <div style={{
                width: "100vw", height: "100vh", background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#0f172a", fontFamily: "system-ui, -apple-system, Roboto", fontSize: 16
            }}>
                This game doesn&apos;t support mobile view.
            </div>
        );
    }

    // language picker overlay BEFORE start
    const showLangPicker = !started || !lang;

    return (
        <div style={{ position: "relative", width: "100%", height: "100vh", overflow: "hidden" }}>
            {/* base gradient background */}
            <div
                style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(-45deg, #dbeafe, #fef3c7, #d1fae5, #ede9fe)",
                    backgroundSize: "400% 400%",
                    animation: "gradientBG 15s ease infinite",
                    zIndex: 0,
                }}
            />

            {/* full-screen takeover video (behind app panel) */}
            {takeover.visible && (
                <video
                    src={takeover.src}
                    autoPlay
                    muted={takeover.muted}
                    loop={!takeover.playOnce}
                    playsInline
                    onLoadedMetadata={(e) => {
                        if (typeof takeover.volume === "number") {
                            try { e.currentTarget.volume = takeover.volume; } catch { }
                        }
                    }}
                    onEnded={() => {
                        if (takeover.playOnce) {
                            try { takeover.cb && takeover.cb(); } catch { }
                            setTakeover({ src: null, visible: false, playOnce: false, cb: null, opacity: 1, ms: 600, muted: true, volume: 1 });
                        }
                    }}
                    style={{
                        position: "fixed", inset: 0,
                        width: "100vw", height: "100vh",
                        objectFit: "cover",
                        zIndex: 0,
                        pointerEvents: "none",
                        opacity: takeover.opacity,
                        transition: `opacity ${takeover.ms}ms ease`,
                    }}
                />
            )}

            {/* app panel (header + card + footer) */}
            <div
                style={{
                    width: "100%", height: "100vh",
                    display: "flex", justifyContent: "center", alignItems: "center",
                    position: "relative", zIndex: 1,
                    opacity: appFade.opacity,
                    transition: `opacity ${appFade.ms}ms ease`,
                }}
            >
                <div
                    style={{
                        width: "min(95vw, 1000px)",
                        height: "min(90vh, 680px)",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
                        display: "flex", flexDirection: "column",
                        justifyContent: "space-between",
                        padding: "16px 20px",
                        position: "relative",
                    }}
                >
                    {/* header */}
                    <header style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        paddingBottom: 8, borderBottom: "1px solid #e2e8f0",
                        fontFamily: "system-ui, -apple-system, Roboto", color: "#0f172a",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ fontWeight: 700 }}>KATIE Labs</div>
                            <span style={{
                                fontSize: 12, padding: "2px 8px", border: "1px solid #bae6fd",
                                borderRadius: 999, background: "#e0f2fe", color: "#0369a1",
                            }}>Beta</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "#334155" }}>
                            <span>Participant ID: <b>K-{Math.random().toString(36).slice(2, 6).toUpperCase()}</b></span>
                            <span style={{ opacity: 0.6 }}>|</span>
                            <span>v1.0.0</span>
                        </div>
                    </header>

                    {/* Phaser mount or language picker */}
                    <div ref={mountRef} style={{
                        flexGrow: 1, display: "flex", justifyContent: "center",
                        alignItems: "center", position: "relative", overflow: "hidden",
                    }}>
                        {showLangPicker && (
                            <div style={{
                                display: "flex", flexDirection: "column", gap: 12,
                                alignItems: "center", justifyContent: "center",
                                fontFamily: "system-ui, -apple-system, Roboto",
                                color: "#0f172a"
                            }}>
                                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
                                    Choose language / 选择语言
                                </div>
                                <div style={{ display: "flex", gap: 10 }}>
                                    <button
                                        onClick={() => setLang("en")}
                                        style={{
                                            padding: "10px 14px", borderRadius: 8, border: "1px solid #cbd5e1",
                                            background: lang === "en" ? "#e0f2fe" : "#fff", cursor: "pointer"
                                        }}
                                    >English</button>
                                    <button
                                        onClick={() => setLang("zh")}
                                        style={{
                                            padding: "10px 14px", borderRadius: 8, border: "1px solid #cbd5e1",
                                            background: lang === "zh" ? "#e0f2fe" : "#fff", cursor: "pointer"
                                        }}
                                    >中文</button>
                                </div>
                                <button
                                    onClick={() => {
                                        if (!lang) return;
                                        localStorage.setItem("katie_lang", lang);
                                        setStarted(true);
                                    }}
                                    style={{
                                        marginTop: 6, padding: "10px 16px", borderRadius: 8,
                                        border: "1px solid #0ea5e9", background: "#0ea5e9", color: "#fff",
                                        cursor: "pointer", fontWeight: 600
                                    }}
                                >
                                    {lang === "zh" ? "开始测试" : "Start Test"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* footer */}
                    <footer style={{
                        textAlign: "center", fontFamily: "system-ui, -apple-system, Roboto",
                        fontSize: 12, color: "#475569", paddingTop: 8, borderTop: "1px solid #e2e8f0",
                    }}>
                        © 2025 KATIE Labs · <span style={{ textDecoration: "underline" }}>Privacy</span> ·{" "}
                        <span style={{ textDecoration: "underline" }}>Terms</span> ·{" "}
                        <span style={{ textDecoration: "underline" }}>Support</span>
                    </footer>
                </div>
            </div>
        </div>
    );
}


